import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  BookOpen, Plus, Search, Filter, Trash2, CheckCircle, X, Eye, 
  Globe, ShieldCheck, Sparkles, Building, User, Award, Layers, 
  ExternalLink, Edit, Check, ChevronDown, ChevronUp
} from 'lucide-react';
import { useAuth } from '../components/AuthGuard';

const SEED_PROGRAMS = [
  {
    id: 'tejati',
    slug: 'program-tejati.html',
    name: 'TEJATI',
    badge: 'Agritech • Autonomous Systems',
    category: 'Agriculture & Robotics',
    concentricTier: 'Tier 3 • Sphere (Agri-Faculties)',
    tagline: 'Sovereign precision agriculture, edge telemetry, and regional harvest liquidity.',
    bannerUrl: 'assets/images/tejati_brand.png',
    description: 'TEJATI is the flagship agricultural engineering initiative of INAPS and DIS. Operating across high-potential farming basins in Kenya, TEJATI equips agronomy students, hardware engineers, and software developers with real-world deployments across solar-powered sensor arrays, low-cost drone imagery, and autonomous soil health telemetry.',
    leadArchitect: 'David Kilonzo',
    leadRole: 'AgTech Solutions Architect',
    leadPhoto: 'assets/images/david_kilonzo.jpg',
    status: 'Published',
    isLive: true,
    specs: [
      { label: 'Concentric Alignment', value: 'Sphere • Agri-Faculties' },
      { label: 'Target Cohorts', value: 'Engineers, Agronomists, Data Pods' },
      { label: 'Field Deployments', value: '12 County Demonstration Hubs' },
      { label: 'Hardware Standards', value: 'LoRaWAN • ESP32 • Edge Compute' }
    ],
    curriculum: [
      { step: '1', title: 'Edge Mesh Soil Telemetry', description: 'Calibration and deployment of LoRaWAN-enabled NPK soil sensors for smallholder yield maximization.' },
      { step: '2', title: 'Autonomous Micro-Drip Irrigation', description: 'Closed-loop solar-powered drip irrigation controllers governed by local edge compute microcontrollers.' },
      { step: '3', title: 'Post-Harvest Cold Chain Liquidity', description: 'Distributed inventory ledgers connecting rural grain aggregation stores with commodity buyers.' },
      { step: '4', title: 'Autonomous Crop Scouting', description: 'Multispectral aerial imaging pods mapping pest infestations directly onto landowner dashboards.' }
    ]
  },
  {
    id: 'fbga',
    slug: 'program-fbga.html',
    name: 'FBGA Annual Capital Summit',
    badge: 'Capital Mobilization • Institutional Governance',
    category: 'Venture Capital & Endowments',
    concentricTier: 'Tier 4 • Orbit (Macro Deal Floor)',
    tagline: 'Faith-Based Governance & AgTech Capital Mobilization Forum.',
    bannerUrl: 'assets/images/fbga_banner.png',
    description: 'The FBGA Summit represents the peak continental orbit for capital deployment. Convening sovereign institutional leaders, ethical endowment funds, university chancellors, and impact investors, the summit structures ethical financing facilities for verified student circles.',
    leadArchitect: 'Bishop Kaloki & Mercy Achieng',
    leadRole: 'Advisory Board Chair & Capital Mobilization Manager',
    leadPhoto: 'assets/images/bishop_kaloki.jpg',
    status: 'Published',
    isLive: true,
    specs: [
      { label: 'Concentric Alignment', value: 'Orbit • Macro Deal Floor' },
      { label: 'Governing Body', value: 'Ecosystem Advisory Board' },
      { label: 'Syndicate Size', value: '24 Institutional Partners' },
      { label: 'Facility Target', value: '$10M+ Catalytic Endowment' }
    ],
    curriculum: [
      { step: '1', title: 'Endowment Capital Structuring', description: 'Guiding institutional foundations on allocating catalytic endowment reserves into student innovation pods.' },
      { step: '2', title: 'Venture Debt & Equipment Leasing', description: 'Structuring debt covenants tailored for early-stage hardware pods purchasing fabrication machinery.' },
      { step: '3', title: 'Live Deal Syndication', description: 'Structured pitch sessions where verified Circles showcase live commercial revenue to institutional allocators.' },
      { step: '4', title: 'Statutory Governance Auditing', description: 'Independent ethical oversight ensuring deployment adheres to the Kenya Companies Act 2015.' }
    ]
  },
  {
    id: 'sam',
    slug: 'program-sam.html',
    name: 'SAM Model (Sovereign Asset & Mentorship)',
    badge: 'Sovereign Incubation • Mentorship Protocol',
    category: 'Incubation & Acceleration',
    concentricTier: 'Tier 2 • Circle (Working Pod)',
    tagline: 'Structured acceleration for high-performing collegiate venture pods.',
    bannerUrl: 'assets/images/sam_primary.webp',
    description: 'The SAM Model is an intensive 16-week venture incubation framework engineered to turn university capstone projects into investable, revenue-generating commercial entities while student founders retain 100% of their core IP.',
    leadArchitect: 'Sam Gichuru',
    leadRole: 'Venture Partner & Startup Mentor',
    leadPhoto: 'assets/images/sam_gichuru.jpg',
    status: 'Published',
    isLive: true,
    specs: [
      { label: 'Concentric Alignment', value: 'Circle • Working Pod' },
      { label: 'Cohort Duration', value: '16 Weeks • Milestone-Driven' },
      { label: 'Equity Policy', value: 'Founder-Centric Sovereign IP' },
      { label: 'Graduation Target', value: 'Seed Commercial Contracts' }
    ],
    curriculum: [
      { step: '1', title: 'Customer Discovery & Validation', description: 'Conducting 50+ enterprise buyer interviews, validating unit pain-points, and prototyping minimal demonstrators.' },
      { step: '2', title: 'Sovereign Architecture & BYOD Vault', description: 'Setting up encrypted zero-knowledge drive vaults and formalizing founder equity agreements under Kenya law.' },
      { step: '3', title: 'Revenue Traction & Deal Floor Prep', description: 'Securing initial paid customer pilot deposits and preparing institutional investment memos.' },
      { step: '4', title: 'Demo Day Syndication', description: 'Graduation presentations at Eternity Centre Karen before accredited angels and enterprise allocators.' }
    ]
  },
  {
    id: 'dkms',
    slug: 'program-dkms.html',
    name: 'DKMS Knowledge Architecture',
    badge: 'Distributed Knowledge • Peer Accreditation',
    category: 'Decentralized Telemetry & Credentials',
    concentricTier: 'Tier 1 • Dot (Atomic Protocol)',
    tagline: 'Decentralized curricular telemetry and verifiable academic credentials.',
    bannerUrl: 'assets/images/dkms_thumb.jpg',
    description: 'DKMS is the cryptographic backbone of INAPS Circles. By moving away from centralized database silos, DKMS enables individual Dots and member universities to maintain cryptographically signed records of learning milestones and peer reviews.',
    leadArchitect: 'Faith Chebet',
    leadRole: 'Distributed Knowledge Systems Architect',
    leadPhoto: 'assets/images/faith_chebet.jpg',
    status: 'Published',
    isLive: true,
    specs: [
      { label: 'Concentric Alignment', value: 'Dot • Atomic Protocol' },
      { label: 'Verification Mode', value: 'Peer Cryptographic Telemetry' },
      { label: 'Interoperability', value: 'W3C Verifiable Credentials' },
      { label: 'Identity Model', value: 'Decentralized Identifiers (DIDs)' }
    ],
    curriculum: [
      { step: '1', title: 'Decentralized Academic Transcripts', description: 'Tamper-evident academic micro-credentials signed by accredited institutional faculty keys in BYOD vaults.' },
      { step: '2', title: 'Zero-Knowledge Skill Proofs', description: 'Allows graduates to mathematically verify competence to employers without exposing sensitive unencrypted data.' },
      { step: '3', title: 'Multi-University Credit Portability', description: 'Enables seamless cross-institutional credit transfer across accredited public and private tertiary colleges.' },
      { step: '4', title: 'Telemetry Telemetry Verification', description: 'Cryptographically audits real git commits and sensor logs into an immutable peer ledger.' }
    ]
  }
];

export default function ProgramManager({ showDemo, setShowDemo }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  // Form Fields
  const [formId, setFormId] = useState('');
  const [formName, setFormName] = useState('');
  const [formBadge, setFormBadge] = useState('');
  const [formCategory, setFormCategory] = useState('Agriculture & Robotics');
  const [formTier, setFormTier] = useState('Tier 3 • Sphere (Agri-Faculties)');
  const [formTagline, setFormTagline] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formBannerUrl, setFormBannerUrl] = useState('');
  const [formLeadArchitect, setFormLeadArchitect] = useState('');
  const [formLeadRole, setFormLeadRole] = useState('');
  const [formStatus, setFormStatus] = useState('Published');

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'programs'));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));

      if (list.length === 0 || showDemo) {
        // If Firestore is empty or demo mode is on, merge seed programs
        const combined = [...SEED_PROGRAMS];
        list.forEach(item => {
          const idx = combined.findIndex(c => c.id === item.id);
          if (idx !== -1) combined[idx] = item;
          else combined.push(item);
        });
        setPrograms(combined);
      } else {
        setPrograms(list);
      }
    } catch (e) {
      console.warn('Could not fetch programs from Firestore, using seed fallback:', e);
      setPrograms(SEED_PROGRAMS);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPrograms();
  }, [showDemo]);

  const handleEdit = (p) => {
    setEditingProgram(p);
    setFormId(p.id);
    setFormName(p.name || '');
    setFormBadge(p.badge || '');
    setFormCategory(p.category || 'Agriculture & Robotics');
    setFormTier(p.concentricTier || 'Tier 3 • Sphere (Agri-Faculties)');
    setFormTagline(p.tagline || '');
    setFormDescription(p.description || '');
    setFormBannerUrl(p.bannerUrl || '');
    setFormLeadArchitect(p.leadArchitect || '');
    setFormLeadRole(p.leadRole || '');
    setFormStatus(p.status || 'Published');
    setIsFormOpen(true);
  };

  const handleNew = () => {
    setEditingProgram(null);
    setFormId('');
    setFormName('');
    setFormBadge('');
    setFormCategory('Agriculture & Robotics');
    setFormTier('Tier 3 • Sphere (Agri-Faculties)');
    setFormTagline('');
    setFormDescription('');
    setFormBannerUrl('');
    setFormLeadArchitect('');
    setFormLeadRole('');
    setFormStatus('Published');
    setIsFormOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formId.trim()) return;

    const payload = {
      id: formId.toLowerCase().trim(),
      slug: `program-${formId.toLowerCase().trim()}.html`,
      name: formName,
      badge: formBadge,
      category: formCategory,
      concentricTier: formTier,
      tagline: formTagline,
      description: formDescription,
      bannerUrl: formBannerUrl,
      leadArchitect: formLeadArchitect,
      leadRole: formLeadRole,
      status: formStatus,
      isLive: formStatus === 'Published',
      updatedAt: new Date().toISOString()
    };

    // Preserve existing specs & curriculum if editing
    if (editingProgram) {
      payload.specs = editingProgram.specs || [];
      payload.curriculum = editingProgram.curriculum || [];
      payload.leadPhoto = editingProgram.leadPhoto || '';
    }

    try {
      await setDoc(doc(db, 'programs', payload.id), payload, { merge: true });
      setStatusMessage('Program saved successfully and published to Site G.');
      setIsFormOpen(false);
      fetchPrograms();
      setTimeout(() => setStatusMessage(''), 4000);
    } catch (err) {
      console.error('Error saving program:', err);
      setStatusMessage('Error saving program to Firestore.');
    }
  };

  const handleToggleLive = async (program) => {
    const newStatus = program.status === 'Published' ? 'Draft' : 'Published';
    try {
      await setDoc(doc(db, 'programs', program.id), {
        status: newStatus,
        isLive: newStatus === 'Published',
        updatedAt: new Date().toISOString()
      }, { merge: true });
      fetchPrograms();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete program "${id}" from dynamic registry?`)) return;
    try {
      await deleteDoc(doc(db, 'programs', id));
      if (selectedProgram?.id === id) setSelectedProgram(null);
      fetchPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
    }
  };

  const filteredPrograms = programs.filter(p => {
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    const query = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (p.name || '').toLowerCase().includes(query) ||
      (p.tagline || '').toLowerCase().includes(query) ||
      (p.leadArchitect || '').toLowerCase().includes(query);
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 text-zinc-100">
      
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-zinc-800">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase tracking-widest rounded-full">
              Dynamic Program Engine
            </span>
            <span className="text-xs text-zinc-400 font-bold">DIS &bull; INAPS Circles</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight text-white flex items-center gap-3">
            <BookOpen className="text-emerald-500" size={30} /> Flagship Programs Registry
          </h1>
          <p className="text-sm text-zinc-400 max-w-2xl mt-1">
            Centrally manage and dynamically update the curriculum tracks, deployment specs, and executive descriptions published across Site G.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowDemo(!showDemo)}
            className={`px-3 py-2 text-xs font-bold rounded-xl border transition-colors cursor-pointer ${
              showDemo ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-zinc-900 text-zinc-400 border-zinc-800'
            }`}
          >
            {showDemo ? 'Demo Mode Active' : 'Show Demo Seeds'}
          </button>
          
          <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-emerald-950/40 transition-all cursor-pointer"
          >
            <Plus size={16} /> New Program
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs font-bold flex items-center gap-2">
          <CheckCircle size={16} /> {statusMessage}
        </div>
      )}

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-zinc-900/60 p-3 rounded-2xl border border-zinc-800">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
          <input
            type="text"
            placeholder="Search programs by name, lead architect, or keyword..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          {['all', 'Agriculture & Robotics', 'Venture Capital & Endowments', 'Incubation & Acceleration', 'Decentralized Telemetry & Credentials'].map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 text-xs font-bold rounded-xl whitespace-nowrap transition-colors cursor-pointer ${
                categoryFilter === cat 
                  ? 'bg-zinc-800 text-white border border-zinc-700 shadow-sm' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {cat === 'all' ? 'All Tracks' : cat.split('&')[0]}
            </button>
          ))}
        </div>
      </div>

      {/* Programs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredPrograms.map((p) => (
          <div 
            key={p.id}
            className="bg-zinc-950 border border-zinc-800 hover:border-zinc-700 rounded-2xl p-5 flex flex-col justify-between transition-all shadow-md group relative"
          >
            <div>
              {/* Badge & Status */}
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {p.badge || p.category}
                </span>

                <button
                  onClick={() => handleToggleLive(p)}
                  className={`text-[10px] font-black uppercase px-2 py-0.5 rounded cursor-pointer transition-colors ${
                    p.status === 'Published' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' 
                      : 'bg-zinc-800 text-zinc-500 border border-zinc-700'
                  }`}
                  title="Click to toggle published status"
                >
                  {p.status || 'Published'}
                </button>
              </div>

              {/* Title & Tier */}
              <h3 className="text-lg font-black text-white group-hover:text-emerald-400 transition-colors">
                {p.name}
              </h3>
              <p className="text-xs text-zinc-400 font-bold mt-0.5 mb-2">{p.concentricTier}</p>

              {/* Tagline & Description snippet */}
              <p className="text-xs text-zinc-300 font-medium line-clamp-2 leading-relaxed">
                {p.tagline || p.description}
              </p>
            </div>

            {/* Footer Metadata */}
            <div className="mt-5 pt-4 border-t border-zinc-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User size={13} className="text-zinc-500" />
                <span className="text-xs text-zinc-400 font-bold">{p.leadArchitect}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setSelectedProgram(p)}
                  className="p-1.5 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-lg transition-colors cursor-pointer"
                  title="View Program Details"
                >
                  <Eye size={14} />
                </button>
                <button
                  onClick={() => handleEdit(p)}
                  className="p-1.5 text-zinc-400 hover:text-emerald-400 bg-zinc-900 border border-zinc-800 rounded-lg transition-colors cursor-pointer"
                  title="Edit Program Fields"
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="p-1.5 text-zinc-400 hover:text-rose-400 bg-zinc-900 border border-zinc-800 rounded-lg transition-colors cursor-pointer"
                  title="Delete Program"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Program Detail Slide-Over Drawer */}
      {selectedProgram && (
        <>
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] transition-opacity"
            onClick={() => setSelectedProgram(null)}
          />
          <div className="fixed inset-y-0 right-0 w-full md:w-[680px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col text-white animate-in slide-in-from-right duration-200">
            {/* Drawer Header */}
            <div className="p-6 border-b border-zinc-800 bg-zinc-900/70 flex items-start justify-between">
              <div>
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-[10px] font-black uppercase tracking-widest rounded-full mb-2 inline-block">
                  {selectedProgram.concentricTier}
                </span>
                <h2 className="text-2xl font-black text-white">{selectedProgram.name}</h2>
                <p className="text-xs text-zinc-400 font-bold mt-1">{selectedProgram.tagline}</p>
              </div>
              <button 
                onClick={() => setSelectedProgram(null)} 
                className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
              <div>
                <h4 className="text-xs font-black uppercase text-zinc-400 tracking-wider mb-2">Full Description</h4>
                <p className="text-zinc-300 leading-relaxed text-xs">{selectedProgram.description}</p>
              </div>

              {/* Specs */}
              {selectedProgram.specs && selectedProgram.specs.length > 0 && (
                <div>
                  <h4 className="text-xs font-black uppercase text-zinc-400 tracking-wider mb-3">Operational Specs</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedProgram.specs.map((s, idx) => (
                      <div key={idx} className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-xl">
                        <span className="text-[10px] font-bold text-zinc-500 uppercase block">{s.label}</span>
                        <span className="text-xs font-bold text-white mt-0.5 block">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Curriculum Modules */}
              {selectedProgram.curriculum && selectedProgram.curriculum.length > 0 && (
                <div>
                  <h4 className="text-xs font-black uppercase text-zinc-400 tracking-wider mb-3">Curriculum &amp; Milestones</h4>
                  <div className="space-y-2">
                    {selectedProgram.curriculum.map((c, idx) => (
                      <div key={idx} className="p-3 bg-zinc-900/40 border border-zinc-800/80 rounded-xl flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-black shrink-0 mt-0.5">
                          {c.step || (idx + 1)}
                        </span>
                        <div>
                          <strong className="text-xs text-white block">{c.title}</strong>
                          <span className="text-xs text-zinc-400 leading-normal">{c.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-900/60 flex items-center justify-between">
              <span className="text-xs text-zinc-500 font-mono">ID: {selectedProgram.id}</span>
              <button
                onClick={() => { setSelectedProgram(null); handleEdit(selectedProgram); }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl cursor-pointer"
              >
                Edit Program Data
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit / Create Modal Form */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 my-8">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <h3 className="text-lg font-black text-white">
                {editingProgram ? `Edit Program: ${editingProgram.name}` : 'Create New Ecosystem Program'}
              </h3>
              <button 
                onClick={() => setIsFormOpen(false)}
                className="p-1 text-zinc-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Program ID / Slug *</label>
                  <input
                    type="text"
                    required
                    disabled={!!editingProgram}
                    value={formId}
                    onChange={(e) => setFormId(e.target.value)}
                    placeholder="e.g. tejati, fbga, sam"
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white font-mono"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Status</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value)}
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white"
                  >
                    <option value="Published">Published (Live)</option>
                    <option value="Draft">Draft (Internal Only)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Program Name *</label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. TEJATI"
                  className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white font-bold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Badge Text</label>
                  <input
                    type="text"
                    value={formBadge}
                    onChange={(e) => setFormBadge(e.target.value)}
                    placeholder="e.g. Agritech • Autonomous Systems"
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Concentric Tier</label>
                  <select
                    value={formTier}
                    onChange={(e) => setFormTier(e.target.value)}
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white"
                  >
                    <option value="Tier 1 • Dot (Atomic Protocol)">Tier 1 • Dot (Atomic Protocol)</option>
                    <option value="Tier 2 • Circle (Working Pod)">Tier 2 • Circle (Working Pod)</option>
                    <option value="Tier 3 • Sphere (Agri-Faculties)">Tier 3 • Sphere (Domain Faculty)</option>
                    <option value="Tier 4 • Orbit (Macro Deal Floor)">Tier 4 • Orbit (Macro Deal Floor)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Tagline</label>
                <input
                  type="text"
                  value={formTagline}
                  onChange={(e) => setFormTagline(e.target.value)}
                  placeholder="Short impact headline"
                  className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white"
                />
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Full Description</label>
                <textarea
                  rows={4}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Detailed programmatic mandate, objectives, and deployment background..."
                  className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white leading-relaxed"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Lead Architect Name</label>
                  <input
                    type="text"
                    value={formLeadArchitect}
                    onChange={(e) => setFormLeadArchitect(e.target.value)}
                    placeholder="e.g. David Kilonzo"
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Lead Architect Role</label>
                  <input
                    type="text"
                    value={formLeadRole}
                    onChange={(e) => setFormLeadRole(e.target.value)}
                    placeholder="e.g. AgTech Solutions Architect"
                    className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Banner Image Asset URL</label>
                <input
                  type="text"
                  value={formBannerUrl}
                  onChange={(e) => setFormBannerUrl(e.target.value)}
                  placeholder="assets/images/tejati_brand.png"
                  className="w-full p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white font-mono text-[11px]"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 font-bold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl shadow-lg cursor-pointer"
                >
                  Save &amp; Publish Dynamic Program
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
